import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Questionnaire } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Button, Drawer, Group, Paper, Stack } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { v4 as uuidv4 } from "uuid";

import { EncounterContext } from "@/contexts/encounter";
import { ProcedureCodingFilterProvider } from "@/encounters/components/EncounterProcedures/components/ProcedureCodingFilterProvider";
import { loadCptQuestionnaireWithCombinedValueSet } from "@/encounters/services/cpt-questionnaire";
import { QuestionFlags } from "@/fhir/enums/question-flags";
import { usePracticeId } from "@/hooks/usePracticeId";
import QuestionnaireForm from "@/intake-forms/components/QuestionnaireForm";
import { useAppDispatch } from "@/store";
import { selectAllProcedures, setProcedure, updateProcedure } from "@/store/slices/procedures";
import ProceduresForm, {
  ProceduresFormType,
  ProceduresFormTypeWithId,
} from "@/zod-schemas/procedures";

type EncounterBillingDrawerProps = {
  opened: boolean;
  onClose: () => void;
  form: UseFormReturnType<ProceduresFormType>;
  targetCptCode?: ProceduresFormTypeWithId;
  handleTargetCptCode: (cptCode: ProceduresFormTypeWithId | undefined) => void;
};

const EncounterBillingDrawer = ({
  opened,
  onClose,
  form,
  targetCptCode,
  handleTargetCptCode,
}: EncounterBillingDrawerProps) => {
  const medplum = useMedplum();
  const dispatch = useAppDispatch();
  const procedures = useSelector(selectAllProcedures);
  const { encounter } = useContext(EncounterContext);
  const practiceId = usePracticeId(encounter);
  const [cptCodeQuestionnaire, setCptCodeQuestionnaire] = useState<Questionnaire | undefined>(
    undefined,
  );

  const updateCptCodeData = (data: ProceduresFormType) => {
    const parsedValues = ProceduresForm.parse(data);

    const procedure = procedures.find((procedure) => procedure.id === targetCptCode?.id);

    if (!procedure) {
      const newProcedure = { ...parsedValues, id: uuidv4(), exists: false };
      dispatch(setProcedure(newProcedure));
    } else {
      dispatch(
        updateProcedure({
          id: procedure.id,
          changes: {
            ...parsedValues,
            id: procedure.id,
            exists: procedure.exists,
            status: procedure.status,
          },
        }),
      );
    }

    onClose();
    handleTargetCptCode(undefined);
    form.reset();
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      const questionnaire = await loadCptQuestionnaireWithCombinedValueSet(medplum, practiceId);
      setCptCodeQuestionnaire(questionnaire);
    };
    fetchInitialData();
  }, [medplum, practiceId]);

  return (
    <Drawer.Root
      size={512}
      offset={8}
      opened={opened}
      position="right"
      onClose={() => {
        form.reset();
        handleTargetCptCode(undefined);
        onClose();
      }}
    >
      <Drawer.Overlay />
      <Drawer.Content data-testid="encounter-billing-procedure-drawer">
        <form onSubmit={form.onSubmit((values) => updateCptCodeData(values))}>
          <Drawer.Header mb="lg" px="md" bg="gray.0">
            <Group w="100%" justify="space-between">
              <Group gap="xs">
                <Drawer.CloseButton
                  size="xl"
                  data-testid="encounter-billing-procedure-drawer-close"
                />
                <Drawer.Title c="dark.9" variant="bold">
                  Procedures
                </Drawer.Title>
              </Group>
              <Button type="submit" data-testid="encounter-billing-procedure-drawer-save">
                Save
              </Button>
            </Group>
          </Drawer.Header>
          <Paper px="lg" pb="xl" radius="md">
            <Stack mt="sm" gap="xl">
              <ProcedureCodingFilterProvider encounter={encounter}>
                <QuestionnaireForm
                  form={form}
                  group={cptCodeQuestionnaire?.item?.[0]}
                  flags={[QuestionFlags.ProceduresUnits, QuestionFlags.ProceduresModifiers]}
                />
              </ProcedureCodingFilterProvider>
            </Stack>
          </Paper>
        </form>
      </Drawer.Content>
    </Drawer.Root>
  );
};

export default EncounterBillingDrawer;
